'use client';

import type React from 'react';
import { useState } from 'react';

import { advertisementRenderLink } from '@/components/advertisement/advertisementLink';
import { demoAdvertisementUrl, useDemoAdvertisement } from '@/components/advertisement/useDemoAdvertisement';

import { AdCategory } from '@cineamo/api/models/advertisement/AdCategory.enum';
import { AdPlacement } from '@cineamo/api/models/advertisement/AdPlacement.enum';
import { AdStatus } from '@cineamo/api/models/advertisement/AdStatus.enum';

import type { AdvertisementPublicData } from '@cineamo/api/helpers/advertisement-helper';

import BannerAdvertisement from '@cineamo/composables/src/advertisement/BannerAdvertisement';
import LayerAdvertisement from '@cineamo/composables/src/advertisement/LayerAdvertisement';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { useUserAgent } from '@/providers/UserAgentProvider';

type DemoAdvertisementProps = {
    type: AdPlacement;
    /** Desktop layer only: pushes it below a host-rendered info banner, as the real one does. */
    isInfoBannerVisible?: boolean;
};

/**
 * The film's campaign creative, in the b2b sales demo's frame only — drawn by the very components that draw a real
 * campaign, so the demo cannot drift from the product: the close button, the "Anzeige" label, the desktop box and
 * the mobile dialog all come from `BannerAdvertisement` and `LayerAdvertisement` as they are.
 *
 * What the demo supplies instead of a campaign is the data and the callbacks. The advertisement is synthesized
 * from one movie id, and `onClick` / `onClose` do nothing but close: no impression, no click, no cookie. A demo
 * view is not a campaign view, and counting one would put invented numbers into the cinema's reporting and into
 * Pulse.
 */
const DemoAdvertisement: React.FC<DemoAdvertisementProps> = ({ type, isInfoBannerVisible }) => {
    const demo = useDemoAdvertisement();
    const domainConfiguration = useDomainConfiguration();
    const isMobileUserAgent = useUserAgent().isMobile;
    const [isClosed, setIsClosed] = useState(false);

    if (!demo || isClosed) {
        return null;
    }

    // `id: 0` never collides with a real advertisement, and nothing here is ever posted anywhere.
    const advertisement: AdvertisementPublicData = {
        id: 0,
        adCategory: AdCategory.FILM,
        status: AdStatus.ACTIVE,
        placement: [AdPlacement.TOP_BANNER, AdPlacement.LAYER],
        endDatetime: '',
        topBannerImageUrl: demoAdvertisementUrl(demo),
        topBannerDesktopImageUrl: demoAdvertisementUrl(demo),
        layerImageUrl: demoAdvertisementUrl(demo, 'layer')
    };
    const close = () => setIsClosed(true);

    if (type === AdPlacement.TOP_BANNER) {
        return (
            <BannerAdvertisement
                advertisement={advertisement}
                onClose={close}
                onClick={() => {}}
                renderLink={advertisementRenderLink}
            />
        );
    }

    return (
        <LayerAdvertisement
            advertisement={advertisement}
            onClose={close}
            onClick={() => {}}
            renderLink={advertisementRenderLink}
            isInfoBannerVisible={isInfoBannerVisible}
            isMobileUserAgent={isMobileUserAgent}
            backdropColor={domainConfiguration.style.backgroundColor}
        />
    );
};

export default DemoAdvertisement;
